// src/components/SystemRecommendation/components/WhyThisSystem.tsx

import React, { useState } from 'react';
import { ReasonCard, ViewMode, Assumption } from '../types';

interface WhyThisSystemProps {
  reasons: ReasonCard[];
  viewMode: ViewMode;
  assumptions: Assumption[];
}

const WhyThisSystem: React.FC<WhyThisSystemProps> = ({
  reasons,
  viewMode,
  assumptions
}) => {
  const [currentCard, setCurrentCard] = useState(0);

  const getCategoryIcon = (category: string) => {
    const icons = {
      fit: '🏠',
      efficiency: '⚡',
      installation: '🔧',
      flexibility: '🔮'
    };
    return icons[category as keyof typeof icons] || '✓';
  };

  const getCategoryTitle = (category: string, viewMode: ViewMode) => {
    if (viewMode === 'customer') {
      const customerTitles = {
        fit: 'DESIGNED FOR YOUR HOME',
        efficiency: 'LOWER YOUR ENERGY BILLS',
        installation: 'QUICK & TIDY INSTALLATION',
        flexibility: 'FUTURE-READY SYSTEM'
      };
      return customerTitles[category as keyof typeof customerTitles] || category.toUpperCase();
    }

    const technicalTitles = {
      fit: 'FIT FOR PROPERTY',
      efficiency: 'EFFICIENCY & RUNNING COST',
      installation: 'INSTALLATION COMPLEXITY',
      flexibility: 'FUTURE FLEXIBILITY'
    };
    return technicalTitles[category as keyof typeof technicalTitles] || category.toUpperCase();
  };

  const hasAssumptions = (reason: ReasonCard) => {
    return reason.assumptions && reason.assumptions.length > 0;
  };

  return (
    <div className="why-this-system">
      <h2 className="section-title">
        {viewMode === 'customer' ? 'Why This System' : 'Why This System Won'}
      </h2>

      <div className="reason-cards-container">
        <div
          className="reason-cards-track"
          style={{ transform: `translateX(-${currentCard * 100}%)` }}
        >
          {reasons.map((reason, index) => (
            <div key={index} className="reason-card">
              <div className="reason-header">
                <span className="reason-icon">
                  {getCategoryIcon(reason.category)}
                </span>
                <h3 className="reason-title">
                  {reason.title || getCategoryTitle(reason.category, viewMode)}
                </h3>
              </div>

              <div className="reason-content">
                <ul className="reason-points">
                  {reason.points.map((point, i) => (
                    <li key={i}>{point}</li>
                  ))}
                </ul>

                {viewMode === 'technical' && hasAssumptions(reason) && (
                  <div className="reason-assumptions">
                    <p className="assumption-label">ℹ️ Assumed:</p>
                    {reason.assumptions!.map((assumpType, i) => {
                      const assumption = assumptions.find(a => a.type === assumpType);
                      return assumption ? (
                        <p key={i} className="assumption-note">
                          {assumption.description}
                        </p>
                      ) : null;
                    })}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="card-indicators">
          {reasons.map((_, index) => (
            <button
              key={index}
              className={`indicator ${index === currentCard ? 'active' : ''}`}
              onClick={() => setCurrentCard(index)}
              aria-label={`View reason ${index + 1}`}
            />
          ))}
        </div>

        <div className="card-navigation">
          <button
            className="nav-button prev"
            onClick={() => setCurrentCard(Math.max(0, currentCard - 1))}
            disabled={currentCard === 0}
          >
            ‹
          </button>
          <button
            className="nav-button next"
            onClick={() => setCurrentCard(Math.min(reasons.length - 1, currentCard + 1))}
            disabled={currentCard === reasons.length - 1}
          >
            ›
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhyThisSystem;
